<script setup lang="ts">
import {
  Check,
  MessageSquare,
  Sprout,
  SquareCheckBig,
  Target,
} from "lucide-vue-next";
import { computed, ref } from "vue";

import RlCard from "@/components/ui/RlCard.vue";
import RlSegmented from "@/components/ui/RlSegmented.vue";
import { overloadCopy, timeOptions } from "@/lib/focusCapacity";
import {
  MARKS_GOAL_OPTIONS,
  type GoalType,
  type SessionMode,
  type SessionScope,
} from "@/lib/focusSession";

/**
 * The session launcher (PRD §6.8 step 1). Presentational: every choice is
 * emitted, nothing is stored here except which paper row is highlighted,
 * which the container has no prop for.
 *
 * Time options are selected **by index**, never by minutes — the absolute
 * values move whenever capacity changes and storing minutes silently desyncs
 * the selection (PE-7.5).
 */

const props = defineProps<{
  scope: SessionScope;
  mode: SessionMode;
  goalType: GoalType;
  goalIndex: number;
  goalMarks: number;
  capacityMinutes: number | null;
  papers: { name: string; meta: string }[];
}>();

const emit = defineEmits<{
  setMode: [SessionMode];
  setGoalType: [GoalType];
  setGoalIndex: [number];
  setGoalMarks: [number];
  setPaper: [number];
  confirm: [];
  close: [];
}>();

const MODES = [
  {
    value: "question" as const,
    icon: SquareCheckBig,
    name: "Question",
    desc: "Answer exam-style questions and get them marked.",
  },
  {
    value: "tutor" as const,
    icon: MessageSquare,
    name: "Tutor",
    desc: "Work through it in conversation.",
  },
  {
    value: "mock" as const,
    icon: Target,
    name: "Mock",
    desc: "Timed paper under exam conditions.",
  },
];

/** What the session is about — the topic when there is one, else the subject. */
const focusName = computed(
  () => props.scope.topicName ?? props.scope.subjectName,
);

const scopeLine = computed(() => {
  if (props.scope.topicName)
    return `${props.scope.subjectName} · ${props.scope.topicName}`;
  return `${props.scope.subjectName} — we'll pick the topic that needs it most.`;
});

const showFocusGoal = computed(() => props.mode !== "mock");

const options = computed(() => timeOptions(props.capacityMinutes));

const timeSegments = computed(() =>
  options.value.map((opt, i) => ({
    value: String(i),
    // One node, not a text node plus an interpolation: a flex gap stacks on
    // the trailing space and doubles it (handoff §6).
    label: opt.stretch ? `${opt.minutes} min · stretch` : `${opt.minutes} min`,
  })),
);

const marksSegments = computed(() =>
  MARKS_GOAL_OPTIONS.map((marks) => ({
    value: String(marks),
    label: `${marks} marks`,
  })),
);

/**
 * The overload rationale, or the cold-start line. Below three completed blocks
 * there is no honest median, so this claims no capacity rather than dressing a
 * guess as a measurement (PE-7.2).
 */
const capacityLine = computed(
  () =>
    overloadCopy(props.capacityMinutes) ??
    "We don't know what you can hold yet — a few more blocks and we will. Start with whatever feels doable.",
);

/** The spoken implementation intention (PE-8.2): said, not just configured. */
const intention = computed(() => {
  if (props.goalType === "marks") {
    return `I'll keep going on ${focusName.value} until I've earned ${props.goalMarks} marks.`;
  }
  const chosen =
    options.value[props.goalIndex] ?? options.value[options.value.length - 1];
  return `For the next ${chosen.minutes} minutes, I'll work on ${focusName.value}.`;
});

const confirmLabel = computed(() => {
  switch (props.mode) {
    case "tutor":
      return "Start tutor session";
    case "mock":
      return "Start mock exam";
    default:
      return "Start question session";
  }
});

// The container has no paper prop — named past papers are deferred (D10) and
// only "Mixed questions" ships — so the highlight is local.
const paperIndex = ref(0);

function pickPaper(index: number): void {
  paperIndex.value = index;
  emit("setPaper", index);
}
</script>

<template>
  <RlCard>
    <div class="plan rl-anim-pop">
      <h2 class="plan-title">Set up your session</h2>
      <p class="plan-scope">{{ scopeLine }}</p>

      <div class="label">Mode</div>
      <div class="modes">
        <button
          v-for="m in MODES"
          :key="m.value"
          type="button"
          class="mode"
          :class="{ 'mode-on': m.value === mode }"
          :aria-pressed="m.value === mode"
          @click="emit('setMode', m.value)"
        >
          <span class="mode-head">
            <span class="mode-icon"><component :is="m.icon" :size="16" /></span>
            <Check v-if="m.value === mode" class="mode-check" :size="15" />
          </span>
          <span class="mode-name">{{ m.name }}</span>
          <span class="mode-desc">{{ m.desc }}</span>
        </button>
      </div>

      <div v-if="showFocusGoal" class="goal">
        <div class="goal-head">
          <Sprout :size="15" />
          <span>Focus goal</span>
        </div>

        <RlSegmented
          class="goal-kind"
          label="Goal type"
          :options="[
            { value: 'time', label: 'Time' },
            { value: 'marks', label: 'Marks' },
          ]"
          :model-value="goalType"
          @update:model-value="emit('setGoalType', $event as GoalType)"
        />

        <template v-if="goalType === 'time'">
          <RlSegmented
            label="Block length"
            :options="timeSegments"
            :model-value="String(goalIndex)"
            @update:model-value="emit('setGoalIndex', Number($event))"
          />
          <p class="goal-note">{{ capacityLine }}</p>
        </template>

        <RlSegmented
          v-else
          label="Marks target"
          :options="marksSegments"
          :model-value="String(goalMarks)"
          @update:model-value="emit('setGoalMarks', Number($event))"
        />

        <p class="goal-intention">{{ `“${intention}”` }}</p>
      </div>

      <div v-else class="exam">
        <div class="exam-head">
          <Target :size="15" />
          <span>Exam conditions</span>
        </div>
        <p class="exam-note">
          Timed, no breaks or focus prompts, marked at the end.
        </p>
        <div class="label label-tight">Paper</div>
        <div class="papers">
          <button
            v-for="(p, i) in papers"
            :key="p.name"
            type="button"
            class="paper"
            :class="{ 'paper-on': i === paperIndex }"
            :aria-pressed="i === paperIndex"
            @click="pickPaper(i)"
          >
            <span class="paper-name">{{ p.name }}</span>
            <span class="paper-meta">{{ p.meta }}</span>
          </button>
        </div>
      </div>

      <div class="plan-actions">
        <button type="button" class="button" @click="emit('close')">
          Cancel
        </button>
        <button
          type="button"
          class="button primary plan-confirm"
          @click="emit('confirm')"
        >
          {{ confirmLabel }}
        </button>
      </div>
    </div>
  </RlCard>
</template>

<style scoped>
.plan {
  max-width: 520px;
  margin: 0 auto;
}

.plan-title {
  margin: 0 0 var(--sp-1);
  font-size: var(--rl-text-xl);
  font-weight: 600;
  letter-spacing: -0.02em;
}

.plan-scope {
  margin: 0 0 var(--sp-5);
  font-size: var(--rl-text-base);
  color: var(--rl-fg-3);
}

.label {
  margin-bottom: var(--sp-2);
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  letter-spacing: 0.08em;
  color: var(--rl-fg-3);
  text-transform: uppercase;
}

.label-tight {
  margin-top: var(--sp-3);
  margin-bottom: var(--sp-2);
}

.modes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-2);
  margin-bottom: var(--sp-5);
}

/* `white-space` and `min-width` undo the global button defaults (rl-txx):
   without them the cards refuse to shrink and the row overflows the plan. */
.mode {
  display: block;
  min-width: 0;
  padding: var(--sp-3);
  white-space: normal;
  text-align: left;
  cursor: pointer;
  background: var(--rl-bg);
  border: 1.5px solid var(--rl-border-subtle);
  border-radius: var(--radius);
  transition:
    border-color var(--t-fast),
    background-color var(--t-fast);
}

.mode:hover {
  background: var(--rl-bg-muted);
}

.mode:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.mode-on {
  background: var(--rl-mint-100);
  border-color: var(--rl-green-600);
}

.mode-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp-2);
}

.mode-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  color: var(--rl-green-700);
  background: var(--rl-mint-100);
  border-radius: var(--radius-sm);
}

.mode-on .mode-icon {
  background: var(--rl-bg);
}

.mode-check {
  color: var(--rl-green-600);
}

.mode-name {
  display: block;
  font-size: var(--rl-text-base);
  font-weight: 600;
  color: var(--rl-fg-1);
}

.mode-desc {
  display: block;
  margin-top: 3px;
  font-size: var(--rl-text-xs);
  line-height: 1.4;
  color: var(--rl-fg-3);
}

.goal,
.exam {
  padding: var(--sp-4);
  margin-bottom: var(--sp-5);
  border: 1px solid var(--rl-mint-300);
  border-radius: var(--radius);
}

.goal {
  background: var(--rl-mint-100);
}

.exam {
  background: var(--rl-warn-bg);
  border-color: var(--rl-warn-bd);
}

.goal-head,
.exam-head {
  display: flex;
  gap: var(--sp-2);
  align-items: center;
  margin-bottom: var(--sp-3);
  font-size: var(--rl-text-base);
  font-weight: 600;
}

.goal-head {
  color: var(--rl-green-800);
}

.exam-head {
  margin-bottom: var(--sp-1);
  color: var(--rl-warn-fg);
}

.goal-kind {
  margin-bottom: var(--sp-3);
}

.goal-note,
.goal-intention {
  margin: var(--sp-3) 0 0;
  font-size: var(--rl-text-sm);
  line-height: 1.5;
  color: var(--rl-green-800);
}

.exam-note {
  margin: 0;
  font-size: var(--rl-text-sm);
  line-height: 1.5;
  color: var(--rl-fg-2);
}

.papers {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.paper {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
  padding: var(--sp-2) var(--sp-3);
  cursor: pointer;
  background: var(--rl-bg);
  border: 1.5px solid var(--rl-border-subtle);
  border-radius: var(--radius-sm);
}

.paper:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.paper-on {
  border-color: var(--rl-warn-fg);
}

.paper-name {
  flex: 1;
  font-size: var(--rl-text-sm);
  font-weight: 500;
  color: var(--rl-fg-1);
  text-align: left;
}

.paper-meta {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  color: var(--rl-fg-3);
}

.plan-actions {
  display: flex;
  gap: var(--sp-2);
}

.plan-confirm {
  flex: 1;
  justify-content: center;
  height: 40px;
}

/* GitHub #378: on a phone the three modes stack as full-width rows (icon,
   name and description, tick on the right) instead of a row to scroll. */
@media (max-width: 640px) {
  .modes {
    grid-template-columns: 1fr;
  }

  .mode {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      "icon name check"
      "icon desc check";
    column-gap: var(--sp-3);
    align-items: center;
  }

  .mode-head {
    display: contents;
  }

  .mode-icon {
    grid-area: icon;
  }

  .mode-check {
    grid-area: check;
  }

  .mode-name {
    grid-area: name;
  }

  .mode-desc {
    grid-area: desc;
  }
}
</style>
